// Utils
import globalLogger from '../../logger';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import { getListChildNewLocation } from '../../../common/elements/utils/elementLocationUtils';
import { getCurrentlyEditingId } from '../selection/currentlyEditingSelector';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import {
    getCaption,
    getColor,
    getContent,
    getElementType,
    getHasClones,
    getLinkedElementId,
    getShowCaption,
    getWidth,
} from '../../../common/elements/utils/elementPropertyUtils';
import { isClone } from '../../../common/elements/utils/elementTypeUtils';
import { asObject } from '../../../common/utils/immutableHelper';
import convertSimpleStringToTiptapContent from '../../../common/tiptap/utils/createJsonContentUtils/convertSimpleStringToTiptapContent';
import { createInitialSingleEditorTaskListContent } from '../../../common/taskLists/singleEditorTaskListContentBuilder';

// Selectors
import { getElements } from '../selectors/elementsSelector';
import { selectIsSingleTaskListEditorEnabledForCurrentUser } from '../taskList/taskListSelector';

// Actions
import { createAndEditElement } from '../actions/elementActions';
import { setLinkElementUrl } from '../link/linkActions';
import { setElementTypeAndUpdateElement } from '../../../common/elements/elementActions';
import { setSelectedElements, startEditingMainEditor } from '../selection/selectionActions';
import { setElementLocalData } from '../local/elementLocalDataActions';

// Constants
import { ElementType } from '../../../common/elements/elementTypes';
import {
    LinkConfig,
    SingleEditorTaskListConfig,
    SwatchConfig,
    TaskConfig,
    UpdateElementTypeConfig,
} from '../../../common/tiptap/extensions/universalCard/universalCardTypes';
import { INITIAL_SWATCH_PROPERTIES } from '../../../common/tiptap/extensions/universalCard/swatch';
import { TiptapContent } from '../../../common/tiptap/tiptapTypes';

const logger = globalLogger.createChannel('universalCardActions');

// Minimal thunk signatures — the shared redux store types in this module are still untyped.
type ThunkDispatch = (action: unknown) => unknown;
type ThunkGetState = () => unknown;

/** Adds the marker removal to type-change actions that dissolve a clone group. */
const clearCloneGroupMarker = (changes: Record<string, unknown> | undefined, shouldClearCloneGroupMarker: boolean) =>
    shouldClearCloneGroupMarker ? { ...changes, hasClones: false } : changes;

const updateElementTypeToLink =
    ({ id, elementType, data }: LinkConfig, shouldClearCloneGroupMarker: boolean) =>
    (dispatch: Function) => {
        const transactionId = getNewTransactionId();

        const { url } = data;

        const rawState = convertSimpleStringToTiptapContent(url);

        const undoChanges = { textContent: rawState, caption: null, showCaption: false, ignoreAutoType: true };

        dispatch(
            setElementTypeAndUpdateElement({
                id,
                elementType,
                undoChanges,
                transactionId,
                changes: clearCloneGroupMarker(undefined, shouldClearCloneGroupMarker),
            }),
        );
        dispatch(setElementLocalData({ id, data: { url } }));
        dispatch(setLinkElementUrl({ id, url, transactionId }));
    };

/**
 * Ensure the tip tap content is created respecting the checked status.
 */
const createSingleTaskListEditorContent = ({ isComplete }: { isComplete: boolean }) => {
    const content = createInitialSingleEditorTaskListContent();
    const taskItem = (content.textContent as TiptapContent).content?.[0]?.content?.[0];

    if (taskItem?.attrs) {
        taskItem.attrs.checked = isComplete;
    }

    return content;
};

const updateElementTypeToTask =
    ({ id, data }: TaskConfig, shouldClearCloneGroupMarker: boolean) =>
    (dispatch: Function, getState: Function) => {
        const transactionId = getNewTransactionId();
        const state = getState();
        const { isComplete } = data.content;

        if (selectIsSingleTaskListEditorEnabledForCurrentUser(state)) {
            const singleTaskListEditorContent = createSingleTaskListEditorContent({ isComplete });
            dispatch(
                setElementTypeAndUpdateElement({
                    id,
                    elementType: ElementType.TASK_LIST_TYPE,
                    transactionId,
                    changes: clearCloneGroupMarker(singleTaskListEditorContent, shouldClearCloneGroupMarker),
                    undoChanges: undefined,
                }),
            );
            dispatch(startEditingMainEditor(id));
            return;
        }

        dispatch(
            setElementTypeAndUpdateElement({
                id,
                elementType: ElementType.TASK_LIST_TYPE,
                transactionId,
                changes: clearCloneGroupMarker(undefined, shouldClearCloneGroupMarker),
                undoChanges: undefined,
            }),
        );
        dispatch(
            createAndEditElement({
                elementType: ElementType.TASK_TYPE,
                location: getListChildNewLocation({ listId: id }),
                content: data.content,
                transactionId,
                select: false,
            }),
        );
        dispatch(setSelectedElements({ ids: [id], rangeAnchors: null, transactionId }));
    };

/**
 * Converts a card into a single-editor task list whose content has already been built - a paste of
 * copied task items into an empty card.
 *
 * Editing moves to the new list's own editor, matching the `[] ` conversion, so the caret lands in
 * the list rather than being left on a card that no longer exists.
 */
const updateElementTypeToSingleEditorTaskList =
    ({ id, data }: SingleEditorTaskListConfig, shouldClearCloneGroupMarker: boolean) =>
    (dispatch: Function) => {
        const transactionId = getNewTransactionId();

        dispatch(
            setElementTypeAndUpdateElement({
                id,
                elementType: ElementType.TASK_LIST_TYPE,
                transactionId,
                changes: clearCloneGroupMarker(data, shouldClearCloneGroupMarker),
                undoChanges: undefined,
            }),
        );

        dispatch(startEditingMainEditor(id));
    };

const updateElementTypeToSwatch =
    ({ id, elementType, data }: SwatchConfig, shouldClearCloneGroupMarker: boolean) =>
    (dispatch: Function, getState: Function) => {
        const { originalText } = data;

        const transactionId = getNewTransactionId();
        const state = getState();

        const elements = getElements(state);
        const currentElement = getElement(elements, id);

        const rawState = convertSimpleStringToTiptapContent(originalText);

        const undoChanges = {
            textContent: rawState,
            caption: getCaption(currentElement),
            showCaption: getShowCaption(currentElement),
            ignoreAutoType: true,
            color: getColor(currentElement),
            media: null,
            width: getWidth(currentElement),
            showColorValue: null,
        };

        return dispatch(
            setElementTypeAndUpdateElement({
                id,
                elementType,
                changes: clearCloneGroupMarker(
                    {
                        ...INITIAL_SWATCH_PROPERTIES,
                        ...data,
                    },
                    shouldClearCloneGroupMarker,
                ),
                undoChanges,
                transactionId,
            }),
        );
    };

/** Detaches loaded clones before the server broadcasts their dissolution, preventing a type-change flash. */
const materialiseLoadedClones = (originalElementId: string) => (dispatch: ThunkDispatch, getState: ThunkGetState) => {
    const elements = getElements(getState());
    const originalElement = getElement(elements, originalElementId);

    if (!originalElement || !getHasClones(originalElement)) return;

    const originalElementType = getElementType(originalElement);
    const originalContent = asObject(getContent(originalElement)) as Record<string, unknown>;

    elements.forEach((element: unknown, cloneId: string | number) => {
        if (!isClone(element) || getLinkedElementId(element) !== originalElementId) return;

        const cloneContent = asObject(getContent(element)) as Record<string, unknown>;

        dispatch(
            setElementTypeAndUpdateElement({
                id: cloneId,
                elementType: originalElementType,
                changes: {
                    ...originalContent,
                    ...cloneContent,
                    hasClones: false,
                    linkTo: null,
                    clonedElementType: null,
                },
                undoChanges: undefined,
                // Keep this local-only and out of the undo stack.
                sync: false,
                transactionId: -1,
            }),
        );
    });
};

export const updateElementType =
    (elementConfig: UpdateElementTypeConfig) => (dispatch: ThunkDispatch, getState: ThunkGetState) => {
        const originalElement = getElement(getElements(getState()), elementConfig.id);
        const shouldClearCloneGroupMarker = getHasClones(originalElement);

        dispatch(materialiseLoadedClones(elementConfig.id));

        switch (elementConfig.elementType) {
            case ElementType.LINK_TYPE:
                return dispatch(updateElementTypeToLink(elementConfig as LinkConfig, shouldClearCloneGroupMarker));
            case ElementType.TASK_TYPE:
                return dispatch(updateElementTypeToTask(elementConfig as TaskConfig, shouldClearCloneGroupMarker));
            case ElementType.TASK_LIST_TYPE:
                return dispatch(
                    updateElementTypeToSingleEditorTaskList(
                        elementConfig as SingleEditorTaskListConfig,
                        shouldClearCloneGroupMarker,
                    ),
                );
            case ElementType.COLOR_SWATCH_TYPE:
                return dispatch(updateElementTypeToSwatch(elementConfig as SwatchConfig, shouldClearCloneGroupMarker));
            default:
                return dispatch(
                    setElementTypeAndUpdateElement({
                        id: elementConfig.id,
                        elementType: elementConfig.elementType,
                        changes: clearCloneGroupMarker(elementConfig.data, shouldClearCloneGroupMarker),
                        undoChanges: undefined,
                        transactionId: undefined,
                    }),
                );
        }
    };

/**
 * Performs an elementType change on the element that's currently being edited.
 */
export const updateCurrentlyEditingElementType =
    (config: Omit<UpdateElementTypeConfig, 'id'>) => (dispatch: Function, getState: Function) => {
        const state = getState();
        const currentlyEditingId = getCurrentlyEditingId(state) as string;

        if (!currentlyEditingId) {
            logger.warn('No currently editing element found');
            return;
        }

        // @ts-expect-error Errors relating to config types not matching...
        return dispatch(updateElementType({ id: currentlyEditingId, ...config }));
    };
